<template>
	<div class="choose-dynamic-data-mapping-card">
		<MappingInterfaceFieldHeader
			:title="`Choose a ${serviceName} mapping block from ${configurationTitle}`"
			:allowChange="hasEditPermissions && matchingWidgetFields?.length > 0"
			@change-mapping="openChangeMappingBlockMappingDialog"
		/>
		<div class="card-content">
			<div v-if="currentMapping" class="width-100">
				<UserSchemaMappingsSelectorField
					:field="currentMapping"
					:editMode="false"
					:service="integrationService"
					:action="action"
					:isMappingDisabled="true"
				/>
			</div>
			<div v-else class="create-mapping-section flex-centerize flex-column width-100 px-3 py-2">
				<div class="description mb-4">No {{ serviceName }} mapping block in {{ configurationType }} setup</div>
				<DashedButton v-if="!loading" @click="createBlock" class="flex-centerize width-100 px-3 button-text">
					Create a {{ serviceName }} mapping block in {{ configurationType }} setup
				</DashedButton>
				<AppLoader size="18px" v-else />
			</div>
		</div>
		<span class="tertiary-link-style text-align-right font-size-sm mt-1 float-right" @click="openSetup">Open setup</span>
		<br />
	</div>
</template>

<script>
// TODO: this can be refactored with ChooseConfigurationFieldAsSource
import { mapGetters, mapActions } from 'vuex';
import { getService } from '@/store/modules/apps/getters';
import { DRAWER_MODES } from '@/store/modules/leftSideDrawer/state';
import { updateWidgetData, addFieldToWidget } from '@/store/modules/widgets/actions';
import { updateNode } from '@/store/modules/nodes/actions';
import {
	userSpecificConfigurationId,
	generalConfigurationId,
	userSpecificConfigurationInterface as userConfWidget,
	generalConfigurationInterface as generalConfWidget,
} from '@/store/modules/selectedWorkflow/modules/configurations/getters';

import DynamicDataService from '@/utils/dynamic-data-service';
import WidgetFieldService from '@/components/BlockEditorWidget/WidgetFieldService';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { FieldType } from '@/components/BlockEditorWidget/widgetFieldTypes.const';
import UserSchemaMappingsSelectorField from '@/components/BlockEditorWidget/WidgetFields/UserSchemaMappingsSelectorField.vue';

import { CredentialsTypes } from '@ply-io/ply-front-shared-utilities';
import MappingInterfaceFieldHeader from '@/components/common/ProfileSelector/MappingInterfaceFieldHeader.vue';

export default {
	name: 'ChooseDynamicDataMappingCard',
	props: {
		contextMode: { type: Boolean, default: false },
		node: { type: Object },
		action: { type: Object },
		serviceId: { type: Number },
		serviceVersionId: { type: String },
		hasEditPermissions: { type: Boolean },
	},
	components: {
		UserSchemaMappingsSelectorField,
		MappingInterfaceFieldHeader,
	},
	computed: {
		...mapGetters([getService.fullPath]),
		...mapGetters([generalConfigurationId.fullPath, userSpecificConfigurationId.fullPath]),
		...mapGetters([userConfWidget.fullPath, generalConfWidget.fullPath]),
		integrationService() {
			return this[getService.fullPath](this.serviceId) || {};
		},
		serviceName() {
			return this.integrationService.service_name;
		},
		isProfileBasedCredentials() {
			return this.node.credentials_configuration.credentials_type === CredentialsTypes.ProfileBased;
		},
		configurationType() {
			return this.isProfileBasedCredentials ? 'User' : 'General';
		},
		widget() {
			return this.isProfileBasedCredentials ? this[userConfWidget.fullPath] : this[generalConfWidget.fullPath];
		},
		widgetFields() {
			return this.widget?.fields.filter((wf) => wf.field_type === FieldType.UserSchemaMappingsSelector);
		},
		userSchemaMappingsSourceFieldUuid() {
			return this.node?.user_schema_mappings_source_field_uuid;
		},
		matchingWidgetFields() {
			return this.widgetFields?.filter(
				(field) => field.metadata.source.service === this.serviceId && field.metadata.source.service_version === this.serviceVersionId
			);
		},
		optionalMapping() {
			return this.matchingWidgetFields?.at(-1);
		},
		currentMapping() {
			// todo: need to check equality with wf.params_map
			return this.matchingWidgetFields?.find((field) => field.id === this.userSchemaMappingsSourceFieldUuid);
		},
		configurationId() {
			return this.node?.credentials_configuration.credentials_type === CredentialsTypes.ProfileBased
				? this[userSpecificConfigurationId.fullPath]
				: this[generalConfigurationId.fullPath]; // CredentialsTypes.AccountBased
		},
		isMappingSetupFinished() {
			return DynamicDataService.isMappingSetupFinished(this.currentMapping, this.integrationService);
		},
		configurationTitle() {
			return this.node?.credentials_configuration.credentials_type === CredentialsTypes.ProfileBased ? 'personal setup' : 'general setup';
		},
	},
	methods: {
		...mapActions('widgetEditor', ['addField']),
		...mapActions(['leftSideDrawer/setLeftSideDrawerState']),
		...mapActions(['selectedNode/setParamInEditNode']),
		...mapActions([updateWidgetData.fullPath, addFieldToWidget.fullPath, updateNode.fullPath]),
		async createBlock() {
			this.loading = true;
			if (this.isMappingSetupFinished) {
				this.openCreateDialog();
			} else {
				const interfaceFieldPayload = {
					fieldType: FieldType.UserSchemaMappingsSelector,
					fieldBody: WidgetFieldService.populateDefaultWidgetValuesIfNeeded({
						field_type: FieldType.UserSchemaMappingsSelector,
						metadata: {
							source: {
								field_item: this.action?.mapped_resource,
								service: this.node.service,
								service_version: this.node.service_version,
								credentials_configuration: {
									credentials_type: this.node.credentials_configuration.credentials_type,
									source_field_uuid: this.node.credentials_configuration.source_field_uuid,
								},
							},
						},
					}),
					configurationTypeSource: this.node?.credentials_configuration?.credentials_type,
				};
				const payload = {
					id: this.widget.id,
					workflowId: this.node.workflow,
					workflowVersionId: this.node.workflow_version,
					interfaceFieldPayload,
					configurationId: this.configurationId,
				};
				const res = await this[addFieldToWidget.fullPath](payload);
				await this.saveSourceFieldInNode(res.field.id);
				this.openCreateDialog();
			}
		},
		async saveSourceFieldInNode(id) {
			if (this.contextMode) {
				const node = { ...this.node, user_schema_mappings_source_field_uuid: id };
				await this[updateNode.fullPath]({ node });
			} else if (this.node.widgetFieldId) {
				if (id && this.node?.user_schema_mappings_source_field_uuid !== id) {
					this.$emit('update-mapper-source', id);
				}
			} else {
				await this['selectedNode/setParamInEditNode']({ key: 'user_schema_mappings_source_field_uuid', value: id });
			}
		},
		openSetup() {
			this['leftSideDrawer/setLeftSideDrawerState'](DRAWER_MODES.test);
		},
		openCreateDialog() {
			const { action, integrationService } = this;
			this.loading = false;
			AppDialogManagerService.openCreateMapperDialog({
				action,
				service: integrationService,
				interfaceFieldUUID: this.currentMapping.id,
			});
		},
		async openChangeMappingBlockMappingDialog() {
			const selectedSourceFieldUuid = await AppDialogManagerService.openChangeMappingBlockMappingDialog({
				mappingBlocksFields: this.matchingWidgetFields,
				sourceFieldUuid: this.userSchemaMappingsSourceFieldUuid,
				configurationId: this.configurationId,
				serviceName: this.serviceName,
			});
			await this.saveSourceFieldInNode(selectedSourceFieldUuid);
		},
	},
	mounted() {
		if (!this.userSchemaMappingsSourceFieldUuid && this.optionalMapping?.id) {
			this.saveSourceFieldInNode(this.optionalMapping.id);
		}
	},
	data() {
		return {
			loading: false,
		};
	},
};
</script>

<style lang="scss" scoped>
.choose-dynamic-data-mapping-card {
	.card-content {
		// height: 105px;
		border-radius: 10px;
		border: 1px solid var(--border-color);
		overflow: hidden;

		.description {
			font-size: 12px;
			font-weight: 600;
			color: var(--profile-selection-grey);
		}
	}
	.create-mapping-section {
		background: var(--setup-node-container-background);
	}
}
.button-text {
	font-size: 14px !important;
}
</style>
